import React from 'react'
import { Form, Formik } from 'formik'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { Controller } from 'react-hook-form'

import { FormsLayout } from 'components/Layout/Forms'
import Button from 'components/Button'
import Dropdown from 'components/Dropdown'
import Datepicker, { useDatepicker } from 'components/Datepicker'

import { formatDateToShort } from 'services/helpers'
import { useMemberNotes } from '../useMemberNotes'
import { useMemberNotesLookup } from './useMemberNotesLookup'
import { useMemberNotesFilterForm } from './useMemberNotesFilterForm'

import { StatusTypes } from 'types/status'
import { NotesFetchParamsTypes } from 'types/notes'
import { DropDownItem } from 'types/components'

import {
  MEMOSNOTES_TYPE_OPTIONS,
  MEMOSNOTES_LEVEL_OPTIONS,
} from 'constants/filters'

const Index = ({
  formStatus,
  formDefaultValues,
  onFilter,
  onFilterClear,
}: {
  formStatus: StatusTypes
  formDefaultValues: NotesFetchParamsTypes
  onFilter: (filterParams: NotesFetchParamsTypes) => void
  onFilterClear: () => void
}): JSX.Element => {
  const { control, handleSubmit, setValue } = useMemberNotesFilterForm({
    defaultValues: formDefaultValues,
  })

  const { startDate, endDate, DatepickerInput, handlePick } = useDatepicker()

  const {
    memberHandleNotesSourceNameLookup,
    memberNotesSourceNameOptionsLoader,
  } = useMemberNotesLookup()

  const isFiltering = formStatus !== 'success' || false

  return (
    <FormsLayout.Wrapper onSubmit={handleSubmit(onFilter)}>
      <FormsLayout.Item>
        <Datepicker
          title="Date"
          name="dateRange"
          onChange={(dates: [Date, Date]) => {
            handlePick(dates)
            setValue(
              'startDate',
              dates[0] ? formatDateToShort(dates[0]) : undefined
            )
            setValue(
              'endDate',
              dates[1] ? formatDateToShort(dates[1]) : undefined
            )
          }}
          selected={startDate}
          startDate={startDate}
          endDate={endDate}
          showYearDropdown
          scrollableYearDropdown
          className="p-2 border border-gray-400 tracking-wider"
          placeholderText="All"
          customInput={<DatepickerInput />}
          selectsRange
        />
      </FormsLayout.Item>
      <FormsLayout.Item>
        <Controller
          name="sourceName"
          control={control}
          render={({ field }) => (
            <Dropdown
              title="Source"
              loadOptions={memberNotesSourceNameOptionsLoader}
              onChange={(value: DropDownItem) => {
                setValue('sourceName', value ? value.value : undefined)
              }}
              // onInputChange={(value) =>
              //   memberHandleNotesSourceNameLookup(value)
              // }
              className="w-full tracking-wider"
              borderColor="#C4C4C4"
              containerClassName="focus:border-primary"
              placeholder="Source names..."
              isClearable
              isSearchable
              async
            />
          )}
        />
      </FormsLayout.Item>
      <FormsLayout.Item>
        <Controller
          name="noteType"
          control={control}
          render={({ field }) => (
            <Dropdown
              title="Type"
              options={MEMOSNOTES_TYPE_OPTIONS}
              className="w-full tracking-wider"
              borderColor="#C4C4C4"
              onSelect={(item: DropDownItem) => {
                setValue('noteType', item.value || undefined)
              }}
            />
          )}
        />
      </FormsLayout.Item>
      <FormsLayout.Item>
        <Controller
          name="priorityLevel"
          control={control}
          render={({ field }) => (
            <Dropdown
              title="Priority Level"
              options={MEMOSNOTES_LEVEL_OPTIONS}
              className="w-full tracking-wider"
              borderColor="#C4C4C4"
              onSelect={(item: DropDownItem) => {
                setValue('priorityLevel', item.value || undefined)
              }}
            />
          )}
        />
      </FormsLayout.Item>
      <FormsLayout.ActionWrapper>
        <Button
          key="submit"
          icon={faSearch}
          iconRight={false}
          type="submit"
          disabled={isFiltering}
          loading={isFiltering}
          className="rounded bg-primary tracking-wider font-bold"
        >
          Search
        </Button>
      </FormsLayout.ActionWrapper>
    </FormsLayout.Wrapper>
  )
}

export default Index
